'use client';

import Link from 'next/link';
import { cn } from '@sharpr/ui';
import { SportButtons } from '@/components/SportButtons';
import { UserMenu } from '@/components/auth/UserMenu';
import { HeaderTourButton } from '@/components/HeaderTourButton';
import { SHARPR_WORDMARK_DIMS } from '@/lib/brand';

interface AppHeaderProps {
  /** Extra classes on the header bar — used for sticky/blur/atmospheric variants. */
  className?: string;
  /** Center the bar contents to max-w-7xl (content pages) vs full-bleed (app shell). */
  contained?: boolean;
}

export function AppHeader({ className, contained = false }: AppHeaderProps) {
  return (
    <header
      className={cn(
        'flex-shrink-0 border-b border-gray-200 dark:border-border-primary py-2 sm:py-3',
        // App shell (odds screen): match the content's horizontal padding below
        // (PlayerSelectionPanel search = px-2.5 sm:px-4) so the logo/user-menu align
        // with the panel + table. Contained content pages keep the wider centered gutter.
        contained ? 'px-4 sm:px-6 lg:px-8' : 'px-2.5 sm:px-4',
        className
      )}
    >
      <div
        className={cn(
          'grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:flex sm:gap-4',
          contained && 'mx-auto w-full max-w-7xl'
        )}
      >
        <Link href="/" aria-label="Home" className="flex items-center shrink-0">
          {/* Mobile shows the compact mark (signed in or out); desktop shows the
              full wordmark. */}
          {/* Intrinsic dims (real PNG sizes) so the imgs reserve their boxes
              before decode — the wordmark is h-N w-auto and would otherwise be
              0px wide at first paint, shifting the sport switcher next to it. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/sharpr-icon.png"
            alt="Sharpr"
            width={256}
            height={256}
            className={cn('h-9 w-9 shrink-0 sm:hidden')}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/sharpr.png"
            alt="Sharpr"
            {...SHARPR_WORDMARK_DIMS}
            className={cn('h-9 w-auto shrink-0 sm:h-12', 'hidden sm:block')}
          />
        </Link>
        <div className="flex justify-start sm:mr-auto">
          <SportButtons />
        </div>
        {/* Fixed-height auth slot: UserMenu swaps between a 40px loading skeleton,
            ~33px anon Sign-in/Start-trial buttons, and a 40px (sm:48px) avatar
            button once the session resolves. Without a pinned height the swap
            resizes the header and shifts the ENTIRE page below it — measured as
            the dominant mobile CLS source on /mlb (anon visitors), p75 0.26.
            h-10 sm:h-12 = the tallest state (skeleton/avatar), so every swap is
            height-neutral; shorter states center within the slot. */}
        <div className="flex items-center gap-1 sm:gap-2 justify-self-end h-10 sm:h-12">
          <HeaderTourButton />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
